// Ficha de un vendedor: su saldo de pagos, sus comisiones por semana, la semana elegida con su
// desglose y sus ventas, y el ajuste manual.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { useState } from 'react';
import type { RetailCommission } from '@fiado/type-kit';
import { Eye } from 'lucide-react';
import { Button, CopyableId, Skeleton, usePermissions } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { ScreenErrorBoundary } from '@/shared/patterns/ScreenErrorBoundary';
import {
    isSellerNotInPayout,
    useLiveSeller,
    usePayoutSeller,
    usePayoutSeries,
} from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import { COMMISSION_PERMISSIONS } from '../../lib/commissionPermissions';
import {
    addDays,
    localToday,
    WEEK_WINDOWS,
    weekLabel,
    weekStart,
    type WeeksWindow,
} from '../../lib/payoutPeriods';
import { multiplierNote, weekChips, whyItems } from '../../lib/sellerWeek';
import { SECTION_TITLE } from '../fieldStyles';
import { AdjustmentModal } from './AdjustmentModal';
import { SellerPaymentsSection } from './SellerPaymentsSection';
import { SellerSalesModal } from './SellerSalesModal';
import { WeeklyBars } from './WeeklyBars';
import { WhyList } from './WhyList';

type Breakdown = RetailCommission.CommissionSellerBreakdown;

// La semana elegida con su desglose. Va aparte para que un fallo de render lo atrape su límite de error.
function WeekSection({
    weekTitle,
    amountCents,
    breakdown,
    loading,
    legacy,
    empty,
    onShowSales,
}: {
    weekTitle: string;
    amountCents: number | null;
    breakdown: Breakdown | undefined;
    loading: boolean;
    legacy: boolean;
    empty: boolean;
    onShowSales: () => void;
}) {
    return (
        <section className="flex flex-col gap-3 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <h3 className={SECTION_TITLE}>Comisión de la semana · {weekTitle}</h3>
                    <p className="text-[24px] font-semibold tabular-nums">
                        {amountCents === null ? '—' : formatMoneyCents(amountCents)}
                    </p>
                </div>
                {breakdown && breakdown.salesCount > 0 && (
                    <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Eye className="size-3.5" aria-hidden />}
                        onClick={onShowSales}
                    >
                        {breakdown.salesCount} ventas
                    </Button>
                )}
            </div>
            {loading ? (
                <Skeleton lines={4} />
            ) : breakdown ? (
                <>
                    <ul className="flex flex-wrap gap-1" aria-label="Resumen de la semana">
                        {weekChips(breakdown).map((chip) => (
                            <li
                                key={chip}
                                className="rounded-full border border-border px-2 py-0.5 text-[10.5px]"
                            >
                                {chip}
                            </li>
                        ))}
                    </ul>
                    {multiplierNote(breakdown) && (
                        <p className="text-[10.5px] text-muted-foreground">
                            Multiplicador {multiplierNote(breakdown)}.
                        </p>
                    )}
                    <h4 className={SECTION_TITLE}>Por qué llegó a este monto</h4>
                    <WhyList items={whyItems(breakdown)} />
                </>
            ) : legacy ? (
                <p className="text-[11px] text-muted-foreground">
                    Este corte se cerró antes del desglose por reglas.
                </p>
            ) : empty ? (
                <p className="text-[11px] text-muted-foreground">Sin movimientos esa semana.</p>
            ) : (
                <p className="text-[11px] text-destructive">No se pudo leer esa semana.</p>
            )}
        </section>
    );
}

export function SellerDetail({
    sellerId,
    directory,
}: {
    sellerId: string;
    directory: CommissionDirectory;
}) {
    const { hasPermission } = usePermissions();
    const [weeks, setWeeks] = useState<WeeksWindow>(12);
    const [selected, setSelected] = useState<string | null>(null);
    const [modal, setModal] = useState<'sales' | 'adjustment' | null>(null);

    const series = usePayoutSeries('SELLER', sellerId, weeks);
    const live = useLiveSeller(sellerId);
    const currentPeriod = live.data?.periodId ?? weekStart(localToday());
    // La serie solo trae cortes cerrados: mientras el anterior no se cierra, va en vivo, como en
    // Mis comisiones.
    const previousPeriod = addDays(currentPeriod, -7);
    const previousOpen =
        series.data !== undefined &&
        !series.data.points.some((point) => point.periodId === previousPeriod);
    const previous = useLiveSeller(previousOpen ? sellerId : null, previousPeriod);
    const periodId = selected ?? currentPeriod;
    const isCurrent = periodId === currentPeriod;
    const isPrevious = previousOpen && periodId === previousPeriod;
    const closed = usePayoutSeller(isCurrent || isPrevious ? null : periodId, sellerId);

    const bars = [
        ...(series.data?.points ?? []).map((point) => ({
            periodId: point.periodId,
            cents: point.paidCents,
            current: false,
            clawback: false,
        })),
        // Sin nada en ese corte (404) va en cero.
        ...(previousOpen
            ? [
                  {
                      periodId: previousPeriod,
                      cents: previous.data?.seller.netCents ?? 0,
                      current: false,
                      clawback: (previous.data?.seller.clawbackCents ?? 0) > 0,
                  },
              ]
            : []),
        {
            periodId: currentPeriod,
            cents: live.data?.seller.netCents ?? 0,
            current: true,
            clawback: (live.data?.seller.clawbackCents ?? 0) > 0,
        },
    ];

    const breakdown = isCurrent
        ? live.data?.seller
        : isPrevious
          ? previous.data?.seller
          : closed.data?.seller.breakdown;
    // Un corte cerrado antes del desglose por reglas solo trae su monto.
    const legacyCents =
        !isCurrent && !isPrevious && closed.data && !closed.data.seller.breakdown
            ? (closed.data.seller.settledAmountCents ?? closed.data.seller.baseAmountCents)
            : null;
    const query = isCurrent ? live : isPrevious ? previous : closed;
    const empty = isSellerNotInPayout(query.error);
    const name = directory.sellerName(sellerId);
    const storeId = directory.sellerStoreId(sellerId);
    const weekKind = isCurrent ? 'Sem. actual' : isPrevious ? 'Sem. sin cerrar' : 'Sem. cerrada';
    const weekTitle = `${weekKind} · ${weekLabel(periodId)}`;
    const amountCents = breakdown?.netCents ?? legacyCents ?? (empty ? 0 : null);

    return (
        <div className="flex h-full min-h-0 flex-col overflow-auto">
            <header className="flex flex-none items-start gap-2 border-b border-border px-4 py-3">
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                        <span className="flex-none">
                            {storeId ? directory.storeName(storeId) : 'Sin tienda'}
                            {directory.isStoreManager(sellerId) ? ' · Admin tienda' : ''} ·
                        </span>
                        <CopyableId inline label="ID" value={sellerId} className="min-w-0" />
                    </div>
                    <h2 className="truncate text-[15px] font-semibold">{name}</h2>
                </div>
                {hasPermission(COMMISSION_PERMISSIONS.adjustmentWrite) && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setModal('adjustment');
                        }}
                    >
                        Abonar bono manual
                    </Button>
                )}
            </header>

            {hasPermission(COMMISSION_PERMISSIONS.paymentRead) && (
                <ScreenErrorBoundary
                    compact
                    title="No se pudieron mostrar los pagos"
                    resetKeys={[sellerId]}
                >
                    <SellerPaymentsSection sellerId={sellerId} />
                </ScreenErrorBoundary>
            )}

            <ScreenErrorBoundary
                compact
                title="No se pudo mostrar el historial de comisiones"
                resetKeys={[sellerId, weeks]}
            >
                <section className="border-b border-border px-4 py-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className={SECTION_TITLE}>
                            Comisiones semanales · últimas {weeks} semanas
                        </h3>
                        <div className="flex gap-1" role="group" aria-label="Semanas de historial">
                            {WEEK_WINDOWS.map((size) => (
                                <Button
                                    key={size}
                                    size="sm"
                                    variant={size === weeks ? 'secondary' : 'ghost'}
                                    aria-pressed={size === weeks}
                                    onClick={() => {
                                        setWeeks(size);
                                    }}
                                >
                                    {size}s
                                </Button>
                            ))}
                        </div>
                    </div>
                    {series.isLoading || previous.isLoading ? (
                        <Skeleton className="h-24" />
                    ) : (
                        <WeeklyBars
                            bars={bars}
                            selected={periodId}
                            onSelect={setSelected}
                            label={`Comisiones semanales de ${name}`}
                        />
                    )}
                    {series.isError && (
                        <p className="mt-1 text-[10.5px] text-destructive">
                            No se pudo leer el historial de cortes.
                        </p>
                    )}
                </section>
            </ScreenErrorBoundary>

            <ScreenErrorBoundary
                compact
                title="No se pudo mostrar esta semana"
                resetKeys={[sellerId, periodId]}
            >
                <WeekSection
                    weekTitle={weekTitle}
                    amountCents={amountCents}
                    breakdown={breakdown}
                    loading={query.isLoading}
                    legacy={legacyCents !== null}
                    empty={empty}
                    onShowSales={() => {
                        setModal('sales');
                    }}
                />
            </ScreenErrorBoundary>

            {modal === 'sales' && (
                <SellerSalesModal
                    sellerId={sellerId}
                    sellerName={name}
                    periodId={isCurrent ? undefined : periodId}
                    weekTitle={weekTitle}
                    directory={directory}
                    onClose={() => {
                        setModal(null);
                    }}
                />
            )}
            {modal === 'adjustment' && (
                <AdjustmentModal
                    sellerId={sellerId}
                    sellerName={name}
                    periodId={currentPeriod}
                    onClose={() => {
                        setModal(null);
                    }}
                />
            )}
        </div>
    );
}
